import { JsonService } from "./JsonService";
import { IHttpClient, FetchHttpClient } from "./HttpClient";

/**
 * The expiry time response for the proof-of-work proof cookie
 */
export interface IProofOfWorkExpiryResponse {
  /**
   * A value indicating whether the user has a valid proof-of-work proof cookie
   */
  hasValidCookie: boolean;

  /**
   * The expiration date (if applicable)
   */
  expiration?: string;
}

/**
 * The proof-of-work service contract
 */
export interface IProofOfWorkService {
  /**
   * The API version
   */
  readonly apiVersion: string;

  /**
   * The URL for fetching a new proof-of-work challenge
   */
  readonly challengeUrl: string;

  /**
   * The URL for submitting the solved challenge
   */
  readonly solveUrl: string;

  /**
   * Gets the expiry time for the user's proof-of-work proof cookie
   *
   * @returns The expiry time response
   */
  getExpiryTimeAsync(): Promise<IProofOfWorkExpiryResponse>;
}

/**
 * The proof-of-work service
 */
export class ProofOfWorkService extends JsonService implements IProofOfWorkService {
  /**
   * Initializes a new instance of the ProofOfWorkService class.
   *
   * @param apiVersion - The API version
   * @param httpClient - The optional HTTP client
   */
  public constructor(apiVersion: string, httpClient: IHttpClient | null = null) {
    super(apiVersion, httpClient ?? new FetchHttpClient());
  }

  /**
   * The URL for fetching a new proof-of-work challenge
   *
   * @returns The challenge URL
   */
  public get challengeUrl(): string {
    return `/api/${this.apiVersion}/proofofwork/challenge`;
  }

  /**
   * The URL for submitting the solved challenge
   *
   * @returns The solve URL
   */
  public get solveUrl(): string {
    return `/api/${this.apiVersion}/proofofwork/solve`;
  }

  /**
   * Gets the expiry time for the user's proof-of-work proof cookie
   *
   * @returns The expiry time response
   */
  public getExpiryTimeAsync(): Promise<IProofOfWorkExpiryResponse> {
    return this.getJsonAsync(`/api/${this.apiVersion}/proofofwork/expiry`, false);
  }
}
